import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { useLoaderData } from "react-router-dom";
import { ClarityProvider } from './clarity';
import { ConfirmProvider } from './components/confirm-dialog';
import Layout from './components/layout';
import { Provider } from "./components/ui/provider";
import { CsrfTokenProvider } from './csrfTokenContext';
import { NavigationGuardProvider } from './navigation-guard';
import { SuperUserProvider } from './super-user-context';

const config = defineConfig({
  globalCss: {
    "*": {
      focusRingColor: "gray.900 !important",
    },
  },
  theme: {
    tokens: {
      colors: {
        primary: {
          50: { value: "#F4EFFF" },
          100: { value: "#E8DEFF" },
          200: { value: "#D2C1FF" },
          300: { value: "#B79BFF" },
          400: { value: "#9A72FF" },
          500: { value: "#621EE1" },
          600: { value: "#5217C2" },
          700: { value: "#42139C" },
          800: { value: "#321074" },
          900: { value: "#230B52" },
          950: { value: "#160737" }
        },
        linkedin: {
          50: "#E8F3FC",
          100: "#C5E0F9",
          200: "#9BCCF6",
          300: "#70B7F2",
          400: "#459EED",
          500: "#0A66C2",  // main
          600: "#084593",
          700: "#063572",
          800: "#04274F",
          900: "#02162B",
        },
        accentBlue: {
          "50": "#f3f8ff",
          "100": "#d0e1ff",
          "200": "#a5c7ff",
          "300": "#71a6ff",
          "400": "#5192ff",
          "500": "#2376ff",
          "600": "#0e60e8",
          "700": "#0b4dbb",
          "800": "#09419e",
          "900": "#072f73"
        },
      },
      fonts: {
        body: { value: "Inter, sans-serif" },
        heading: { value: "Inter, sans-serif" }
      },
      animations: {
        topLoader: { value: "topLoader 1s ease-in-out infinite" },
      }
    },
    semanticTokens: {
      colors: {
        gray: {
          focusRing: { value: "gray.900" },
        },
        primary: {
          contrast: { value: "{white}" },
          fg: { value: "{colors.primary.700}" },
          subtle: { value: "{colors.primary.100}" },
          muted: { value: "{colors.primary.200}" },
          emphasized: { value: "{colors.primary.300}" },
          solid: { value: "{colors.primary.600}" },
          focusRing: { value: "{colors.primary.500}" },
          border: { value: "{colors.primary.200}" }
        },
        linkedin: {
          contrast: { value: "{white}" },
          fg: { value: "{colors.linkedin.700}" },
          subtle: { value: "{colors.linkedin.100}" },
          muted: { value: "{colors.linkedin.200}" },
          emphasized: { value: "{colors.linkedin.300}" },
          solid: { value: "{colors.linkedin.600}" },
          focusRing: { value: "{colors.linkedin.500}" },
          border: { value: "{colors.linkedin.500}" }
        },
      },
      spacing: {
        "sectionGap": { value: "spacing.4" },
        "workspaceGap": { value: "spacing.6" },
        "layoutGap": { value: "spacing.5" },
        "layoutHorizontalMargin": { value: "spacing.5" },
        "layoutVerticalMargin": { value: "spacing.5" }
      }
    },
    keyframes: {
      topLoader: {
        "0%": {
          left: "0%",
          width: "0%"
        },
        "50%": {
          left: "0%",
          width: "100%"
        },
        "100%": {
          left: "100%",
          width: "0%"
        }
      }
    }
  }
});

const system = createSystem(defaultConfig, config);

function App() {
  const { organization, user, csrfToken, channels, campaigns, companyPages, environment, orgID, importSharesMaxImportCount, importSharesMaxAgeDays, organizations } = useLoaderData()
  return (
    <Provider theme={system} enableSystem={false}>
      <ConfirmProvider>
        <NavigationGuardProvider>
        <CsrfTokenProvider csrfToken={csrfToken}>
          <SuperUserProvider orgID={orgID}>
            <ClarityProvider environment={environment}>
              <Layout organization={organization} user={user} csrfToken={csrfToken} channels={channels} campaigns={campaigns} companyPages={companyPages} orgID={orgID} importSharesMaxImportCount={importSharesMaxImportCount} importSharesMaxAgeDays={importSharesMaxAgeDays} organizations={organizations} />
            </ClarityProvider>
          </SuperUserProvider>
        </CsrfTokenProvider>
        </NavigationGuardProvider>
      </ConfirmProvider>
    </Provider >
  );
}

export default App;
